import { saveFileToBrowserStorage, loadFileFromBrowserStorage } from '../app/fileStorage.js';
import {
  FLUVERIA_FILE_EXTENSION,
  deserializeFile,
  serializeFileBinary,
} from '../app/file.js';
import { importFileLabel, loadFromStorageLabel } from '../editor/historyLabels.js';
import { initThemeControl } from './theme.js';
import { initUiScaleControl } from './uiScale.js';
import { createDragger } from './controls/dragField.js';
import type { NodeEditorController } from './nodeEditor.js';
import type { AppState, EditorGraphMode, ViewportMode, ViewportTarget } from '../state/appState.js';
import { resolveArcCoverageMode } from '../sdf/arcCoverageMode.js';
import { isPerfLogEnabled, setPerfLogEnabled } from '../debug/perfLog.js';
import type { HistoryStore } from '../editor/historyState.js';
import { syncViewportRenderModeControls } from './viewportRenderModeControls.js';
import { getViewportTargetLabel, isViewportTargetValid } from '../state/viewportTargets.js';
import { setViewportRulersVisible } from '../app/viewportFrame.js';
import {
  loadViewportRenderTheme,
  getViewportRenderTheme,
  setViewportRenderTheme,
  type ViewportRenderTheme,
} from '../app/viewportRenderTheme.js';
import { onColorSchemeChange } from '../app/colorScheme.js';

interface ToolbarDeps {
  appState: AppState;
  historyStore: Pick<HistoryStore, 'performUndo' | 'performRedo' | 'resetHistory'>;
  setEditorGraphMode: (mode: EditorGraphMode) => void;
  clearApp: () => void;
  syncEditor: () => void;
  nodeEditor: Pick<NodeEditorController, 'renderGraph'>;
  renderEditorNavigation: () => void;
  renderSceneTree: () => void;
  resetRenderState: () => void;
  updateParams: () => void;
  scheduleRender: () => void;
  reloadDataSources?: () => Promise<void>;
  getViewportFocusTarget?: () => ViewportTarget | null;
}

const VIEWPORT_VIEW_OPTIONS_STORAGE_KEY = 'fluveria-viewport-view-options';
const VIEWPORT_RENDER_MODE_STORAGE_KEY = 'fluveria-viewport-render-mode';

function loadStoredViewportRenderMode(): EditorGraphMode | null {
  const raw = localStorage.getItem(VIEWPORT_RENDER_MODE_STORAGE_KEY);
  return raw === 'auto' || raw === 'geometry' || raw === 'material' ? raw : null;
}

function saveViewportRenderMode(mode: EditorGraphMode): void {
  localStorage.setItem(VIEWPORT_RENDER_MODE_STORAGE_KEY, mode);
}

/** Apply the persisted viewport render mode (auto/geometry/material). Call after the file loads, which resets the mode. */
export function applyStoredViewportRenderMode(
  appState: AppState,
  setEditorGraphMode: (mode: EditorGraphMode) => void,
  getViewportFocusTarget?: () => ViewportTarget | null,
): void {
  const stored = loadStoredViewportRenderMode();
  if (!stored) return;
  setEditorGraphMode(stored);
  syncViewportToolbarControls(appState, getViewportFocusTarget);
}

const viewportViewOptions = {
  overlay: true,
  rulers: false,
};

type ViewportViewOption = keyof typeof viewportViewOptions;

function loadViewportViewOptions(): void {
  try {
    const raw = localStorage.getItem(VIEWPORT_VIEW_OPTIONS_STORAGE_KEY);
    if (!raw) return;
    const stored = JSON.parse(raw) as Partial<Record<ViewportViewOption, unknown>>;
    if (typeof stored.overlay === 'boolean') viewportViewOptions.overlay = stored.overlay;
    if (typeof stored.rulers === 'boolean') viewportViewOptions.rulers = stored.rulers;
  } catch {
    // Ignore malformed preferences; the defaults are safe and visible.
  }
}

function saveViewportViewOptions(): void {
  localStorage.setItem(VIEWPORT_VIEW_OPTIONS_STORAGE_KEY, JSON.stringify(viewportViewOptions));
}

function syncViewportViewOptionControls(): void {
  const viewportArea = document.getElementById('viewport-area');
  if (viewportArea) {
    viewportArea.dataset.overlayVisible = viewportViewOptions.overlay ? 'true' : 'false';
    viewportArea.dataset.rulersVisible = viewportViewOptions.rulers ? 'true' : 'false';
  }
  setViewportRulersVisible(viewportViewOptions.rulers);

  document.querySelectorAll<HTMLButtonElement>('[data-viewport-view-option]').forEach(button => {
    const option = button.dataset.viewportViewOption as ViewportViewOption | undefined;
    if (option !== 'overlay' && option !== 'rulers') return;
    const active = viewportViewOptions[option];
    button.classList.toggle('active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.title = active
      ? `Hide ${option === 'overlay' ? 'overlay' : 'rulers'}`
      : `Show ${option === 'overlay' ? 'overlay' : 'rulers'}`;
  });
}

function syncViewportRenderThemeControls(): void {
  const current = getViewportRenderTheme();
  document.querySelectorAll<HTMLButtonElement>('[data-viewport-render-theme]').forEach(button => {
    const theme = button.dataset.viewportRenderTheme;
    const active = theme === current;
    button.classList.toggle('active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
  });
}

function syncViewportSizeControls(appState: AppState): void {
  const modeSelect = document.getElementById('viewport-size-mode-select') as HTMLSelectElement | null;
  const fixedSelect = document.getElementById('res-select') as HTMLSelectElement | null;
  if (modeSelect) modeSelect.value = appState.viewportSizeMode;
  if (fixedSelect) {
    fixedSelect.value = String(appState.resolution);
    fixedSelect.disabled = appState.viewportSizeMode === 'fit';
  }
  const coverageSelect = document.getElementById('arc-coverage-select') as HTMLSelectElement | null;
  if (coverageSelect) coverageSelect.value = appState.arcCoverageMode;
}

function getViewportDisplayTarget(
  appState: AppState,
  getViewportFocusTarget?: () => ViewportTarget | null,
): ViewportTarget | null {
  if (appState.viewportMode === 'focus') return getViewportFocusTarget?.() ?? null;
  if (appState.viewportMode === 'isolation') return appState.viewportIsolationTarget;
  return null;
}

export function applyViewportModeSelection(
  appState: AppState,
  mode: ViewportMode,
  getViewportFocusTarget?: () => ViewportTarget | null,
): void {
  if (mode === 'isolation') {
    if (appState.viewportMode === 'isolation') {
      appState.viewportMode = 'focus';
      appState.viewportIsolationTarget = null;
      return;
    }
    appState.viewportMode = 'isolation';
    appState.viewportIsolationTarget = getViewportFocusTarget?.() ?? { type: 'scene' };
    return;
  }

  appState.viewportMode = mode;
  appState.viewportIsolationTarget = null;
}

function syncViewportModeControls(
  appState: AppState,
  getViewportFocusTarget?: () => ViewportTarget | null,
): void {
  document.querySelectorAll<HTMLButtonElement>('[data-viewport-mode]').forEach(button => {
    const mode = button.dataset.viewportMode;
    const active = mode === appState.viewportMode;
    button.classList.toggle('active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
  });

  const targetLabel = document.getElementById('viewport-target-label');

  const target = getViewportDisplayTarget(appState, getViewportFocusTarget);
  if (targetLabel) {
    const hidden = appState.viewportMode === 'scene';
    const missing = !target || !isViewportTargetValid(appState, target);
    targetLabel.hidden = hidden;
    targetLabel.classList.toggle('missing', missing);
    targetLabel.textContent = target
      ? getViewportTargetLabel(appState, target)
      : (appState.viewportMode === 'focus' ? 'No focus target' : 'No isolated target');
    targetLabel.title = targetLabel.textContent;
  }
}

export function syncViewportToolbarControls(
  appState: AppState,
  getViewportFocusTarget?: () => ViewportTarget | null,
): void {
  syncViewportRenderModeControls(appState);
  syncViewportRenderThemeControls();
  syncViewportSizeControls(appState);
  syncViewportModeControls(appState, getViewportFocusTarget);
  syncViewportViewOptionControls();
}

export function revealViewportToolbar(): void {
  const toolbar = document.getElementById('viewport-toolbar');
  if (!toolbar) return;
  requestAnimationFrame(() => {
    toolbar.classList.remove('viewport-toolbar-pending');
  });
}

function refreshFileViews(appState: AppState, deps: ToolbarDeps): void {
  syncViewportToolbarControls(appState, deps.getViewportFocusTarget);
  deps.nodeEditor.renderGraph();
  deps.updateParams();
  deps.renderSceneTree();
  deps.renderEditorNavigation();
  deps.scheduleRender();
}

function downloadBinaryFile(filename: string, bytes: Uint8Array): void {
  const blob = new Blob([bytes as BlobPart], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function createFileInput(onLoad: (data: Uint8Array) => void | Promise<void>): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = `.${FLUVERIA_FILE_EXTENSION},.json,application/json`;
  input.style.display = 'none';
  input.addEventListener('change', async () => {
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    await onLoad(new Uint8Array(await file.arrayBuffer()));
  });
  document.body.appendChild(input);
  return input;
}

export function initToolbar(deps: ToolbarDeps): void {
  const { appState } = deps;
  const syncControls = () => syncViewportToolbarControls(appState, deps.getViewportFocusTarget);
  initThemeControl();
  initUiScaleControl();
  loadViewportViewOptions();
  loadViewportRenderTheme();
  const fileInput = createFileInput(async data => {
    try {
      deserializeFile(appState, data);
    } catch (err) {
      console.error(err);
      alert('Could not import file.');
      return;
    }
    deps.historyStore.resetHistory(importFileLabel());
    try {
      await deps.reloadDataSources?.();
    } catch (error) {
      console.error(error);
      alert('Document opened, but its data sources could not load.');
    }
    refreshFileViews(appState, deps);
  });

  document.querySelectorAll<HTMLButtonElement>('[data-viewport-render-mode]').forEach(button => {
    button.addEventListener('click', () => {
      const mode = button.dataset.viewportRenderMode;
      if (mode !== 'auto' && mode !== 'geometry' && mode !== 'material') return;
      saveViewportRenderMode(mode);
      deps.setEditorGraphMode(mode);
      syncControls();
      deps.nodeEditor.renderGraph();
      deps.updateParams();
      deps.renderSceneTree();
      deps.renderEditorNavigation();
      deps.scheduleRender();
    });
  });
  document.querySelectorAll<HTMLButtonElement>('[data-viewport-render-theme]').forEach(button => {
    button.addEventListener('click', () => {
      const theme = button.dataset.viewportRenderTheme;
      if (theme !== 'system' && theme !== 'dark' && theme !== 'light') return;
      setViewportRenderTheme(theme as ViewportRenderTheme);
      syncViewportRenderThemeControls();
      deps.scheduleRender();
    });
  });
  onColorSchemeChange(() => {
    if (getViewportRenderTheme() === 'system') deps.scheduleRender();
  });
  document.querySelectorAll<HTMLButtonElement>('[data-viewport-mode]').forEach(button => {
    button.addEventListener('click', () => {
      const mode = button.dataset.viewportMode;
      if (mode !== 'scene' && mode !== 'focus' && mode !== 'isolation') return;
      applyViewportModeSelection(appState, mode as ViewportMode, deps.getViewportFocusTarget);
      syncControls();
      deps.scheduleRender();
    });
  });
  document.querySelectorAll<HTMLButtonElement>('[data-viewport-view-option]').forEach(button => {
    button.addEventListener('click', () => {
      const option = button.dataset.viewportViewOption as ViewportViewOption | undefined;
      if (option !== 'overlay' && option !== 'rulers') return;
      viewportViewOptions[option] = !viewportViewOptions[option];
      saveViewportViewOptions();
      syncViewportViewOptionControls();
      if (option === 'rulers') deps.scheduleRender();
    });
  });
  syncControls();
  (document.getElementById('colormap-select') as HTMLSelectElement).addEventListener('change', e => {
    appState.colormap = (e.target as HTMLSelectElement).value as 'gray' | 'inferno' | 'plasma' | 'turbo';
    deps.scheduleRender();
  });
  (document.getElementById('viewport-size-mode-select') as HTMLSelectElement).addEventListener('change', e => {
    appState.viewportSizeMode = (e.target as HTMLSelectElement).value as AppState['viewportSizeMode'];
    syncControls();
    deps.scheduleRender();
  });
  (document.getElementById('res-select') as HTMLSelectElement).addEventListener('change', e => {
    appState.resolution = parseInt((e.target as HTMLSelectElement).value);
    syncControls();
    deps.scheduleRender();
  });
  (document.getElementById('backend-select') as HTMLSelectElement).addEventListener('change', e => {
    appState.backend = (e.target as HTMLSelectElement).value as 'canvas' | 'webgpu';
    deps.resetRenderState();
    deps.scheduleRender();
  });
  (document.getElementById('aa-select') as HTMLSelectElement).addEventListener('change', e => {
    appState.aaMode = (e.target as HTMLSelectElement).value as 'none' | 'fwidth' | 'msaa2x';
    deps.scheduleRender();
  });
  const perfLogSelect = document.getElementById('perf-log-select') as HTMLSelectElement;
  perfLogSelect.value = isPerfLogEnabled() ? 'on' : 'off';
  perfLogSelect.addEventListener('change', e => {
    setPerfLogEnabled((e.target as HTMLSelectElement).value === 'on');
  });
  (document.getElementById('arc-coverage-select') as HTMLSelectElement).addEventListener('change', e => {
    appState.arcCoverageMode = resolveArcCoverageMode((e.target as HTMLSelectElement).value);
    syncControls();
    deps.resetRenderState();
    deps.scheduleRender();
  });
  const aaWidthDragger = createDragger(appState.aaWidth ?? 0.5, 0.1, 4.0, 0.1, v => {
    appState.aaWidth = v;
    deps.scheduleRender();
  }, undefined, { subtype: 'distance' });
  aaWidthDragger.classList.add('settings-drag-field');
  (document.getElementById('aa-width-range') as HTMLElement).replaceWith(aaWidthDragger);

  document.getElementById('save-btn')!.addEventListener('click', () => {
    deps.syncEditor();
    saveFileToBrowserStorage(appState);
  });

  document.getElementById('export-btn')?.addEventListener('click', () => {
    deps.syncEditor();
    downloadBinaryFile(`fluveria-file.${FLUVERIA_FILE_EXTENSION}`, serializeFileBinary(appState));
  });

  document.getElementById('import-btn')?.addEventListener('click', () => {
    fileInput.click();
  });

  document.getElementById('undo-btn')!.addEventListener('click', () => {
    if (!deps.historyStore.performUndo()) return;
    refreshFileViews(appState, deps);
  });

  document.getElementById('redo-btn')!.addEventListener('click', () => {
    if (!deps.historyStore.performRedo()) return;
    refreshFileViews(appState, deps);
  });

  document.getElementById('load-btn')!.addEventListener('click', async () => {
    if (!loadFileFromBrowserStorage(appState)) return;
    deps.historyStore.resetHistory(loadFromStorageLabel());
    try {
      await deps.reloadDataSources?.();
    } catch (error) {
      console.error(error);
      alert('Document opened, but its data sources could not load.');
    }
    refreshFileViews(appState, deps);
  });

  document.getElementById('clear-btn')!.addEventListener('click', () => {
    deps.clearApp();
    refreshFileViews(appState, deps);
  });
}
